import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { API, usePageTitle, useShop } from "@/context/ShopContext";
import ProductCard from "@/components/ProductCard";

export default function Search() {
  usePageTitle("Search");
  const { meta } = useShop();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    const query = q.trim();
    if (!query) {
      setResults(null);
      return;
    }
    const t = setTimeout(() => {
      fetch(`${API}/products?q=${encodeURIComponent(query)}`)
        .then((r) => r.json())
        .then((d) => setResults(d.products || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20" data-testid="search-page">
      <header className="max-w-2xl">
        <p className="overline-label mb-4">Search</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">What Are You Looking For?</h1>
        <div className="relative mt-10">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-taupe" strokeWidth={1.5} />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search shades, fabrics, collections…"
            autoFocus
            className="input-lux !pl-11 !py-4 !text-base"
            aria-label="Search products"
          />
        </div>
      </header>

      {results === null ? (
        <div className="mt-14" data-testid="search-suggestions">
          <p className="overline-label mb-6">Popular Shades</p>
          <div className="flex flex-wrap gap-4">
            {meta.shades.slice(0, 8).map((s) => (
              <button
                key={s.name}
                data-testid={`search-shade-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setQ(s.name)}
                className="inline-flex items-center gap-2.5 border border-beige px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-espresso/80 hover:border-espresso hover:text-espresso transition-colors"
              >
                <span className="w-4 h-4 rounded-full border border-beige" style={{ backgroundColor: s.hex }} />
                {s.name}
              </button>
            ))}
          </div>
        </div>
      ) : results.length === 0 ? (
        <div className="py-20 text-center" data-testid="search-empty">
          <p className="font-serif text-2xl">Nothing found for “{q}”</p>
          <p className="text-sm text-espresso/60 mt-3">Try a shade, a fabric, or a colour family.</p>
        </div>
      ) : (
        <div className="mt-14">
          <p className="text-xs uppercase tracking-[0.2em] text-taupe mb-8" data-testid="search-count">
            {results.length} {results.length === 1 ? "result" : "results"} for “{q}”
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-12" data-testid="search-results">
            {results.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
